import { useId, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/Button'

export interface FieldNote { id: string; content: string; createdAt: string | null }
export interface FieldNotesData { notes: FieldNote[]; hasMore: boolean }
export interface FieldNotesTransport {
  list: (jobId: string) => Promise<FieldNotesData>
  add: (jobId: string, content: string) => Promise<FieldNote>
}
const liveTransport: FieldNotesTransport = {
  list: async (id) => (await apiClient.get<{ data: FieldNotesData }>(`/api/jobs/${id}/field-notes`)).data.data,
  add: async (id, content) => (await apiClient.post<{ data: FieldNote }>(`/api/jobs/${id}/field-notes`, { content })).data.data,
}

export function JobFieldNotes({ jobId, canAdd, transport = liveTransport }: {
  jobId: string; canAdd: boolean; transport?: FieldNotesTransport
}) {
  // Keyed child ensures drafts/errors never move to another job.
  return <FieldNotes key={jobId} jobId={jobId} canAdd={canAdd} transport={transport} />
}
function FieldNotes({ jobId, canAdd, transport }: { jobId: string; canAdd: boolean; transport: FieldNotesTransport }) {
  const client = useQueryClient()
  const labelId = useId()
  const [draft, setDraft] = useState('')
  const [saved, setSaved] = useState(false)
  const submitting = useRef(false)
  const queryKey = ['job-field-notes', jobId]
  const query = useQuery({ queryKey, queryFn: () => transport.list(jobId), retry: false })
  const mutation = useMutation({
    mutationFn: (content: string) => transport.add(jobId, content),
    retry: false,
    onSuccess: (note) => {
      client.setQueryData<FieldNotesData>(queryKey, (old) => {
        const notes = [note, ...(old?.notes ?? []).filter((n) => n.id !== note.id)]
        return { notes: notes.slice(0, 100), hasMore: Boolean(old?.hasMore) || notes.length > 100 }
      })
      setDraft('')
      setSaved(true)
      void client.invalidateQueries({ queryKey })
    },
  })
  return <section className="ds-tile-inset p-4 space-y-4 animate-fade-up" aria-labelledby={labelId}>
    <div>
      <h3 id={labelId} className="font-semibold">Field notes</h3>
      <p className="text-sm text-midnight-text3">Short updates for this job. Newest first.</p>
    </div>
    {query.isPending ? <p role="status">Loading field notes…</p> : query.isError ? (
      <div role="alert">
        <p>Field notes could not be loaded.</p>
        <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>Retry notes</Button>
      </div>
    ) : <>
      {query.data.notes.length === 0 ? <p>No field notes yet.{canAdd ? ' Add the first site update.' : ''}</p> : (
        <ul className="space-y-3" aria-label="Field notes">
          {query.data.notes.map((note) => <li key={note.id} className="border-b border-[var(--border)] pb-3">
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{note.content}</p>
            <p className="text-sm text-midnight-text3 mt-1">{note.createdAt && Number.isFinite(Date.parse(note.createdAt))
              ? <time dateTime={note.createdAt}>{new Date(note.createdAt).toLocaleString()}</time>
              : 'Time not recorded'}</p>
          </li>)}
        </ul>
      )}
      {query.data.hasMore && <p className="text-sm text-midnight-text3">Showing the latest 100 notes. Older notes are not shown.</p>}
    </>}
    {canAdd && <form className="space-y-2" onSubmit={async (event) => {
      event.preventDefault()
      const content = draft.trim()
      if (!content || content.length > 1000 || submitting.current || query.isError || query.isPending) return
      submitting.current = true
      setSaved(false)
      try { await mutation.mutateAsync(content) } catch { /* Keep draft and show inline failure. */ }
      finally { submitting.current = false }
    }}>
      <label className="block text-sm font-medium" htmlFor={`${labelId}-draft`}>New field note</label>
      <textarea id={`${labelId}-draft`} className="input-field w-full text-base" rows={3} maxLength={1000}
        value={draft} disabled={mutation.isPending} onChange={(event) => { setDraft(event.target.value); setSaved(false) }}
        placeholder="What should the team know?" aria-describedby={`${labelId}-help`} />
      <p id={`${labelId}-help`} className="text-sm text-midnight-text3">{draft.length}/1000 · Text only. Use job photos for attachments.</p>
      {mutation.isError && <p role="alert">Note could not be saved. Your draft is still here. Check your connection before trying again.</p>}
      {saved && <p role="status">Field note added.</p>}
      <Button type="submit" needsNetwork loading={mutation.isPending}
        disabled={!draft.trim() || query.isPending || query.isError}>Add note</Button>
    </form>}
  </section>
}
